import { useEffect, useId, useRef, useState } from 'react';
import useLatestRef from '../shared/useLatestRef';
import { notifyOfferTokenInserted, offerIndexOf } from '../shared/offerTokens';
import { CreationPayload, InitialPayload } from '../shared/payloadReaders';
import { CashdeskBlock, NewBlock, buildBlock, readBlocks } from './cashdeskBlocks';

interface UseCashdeskBlocksOptions {
  initialPayload?: InitialPayload;
  offerCount: number;
  onAddOffer?: () => void;
  onOfferUsed?: (offerIndex: number) => void;
  onOfferIndicesChange?: (indices: Set<number>) => void;
  onOfferRemoved?: (offerIndex: number) => void;
  onPayloadChange?: (payload: CreationPayload) => void;
}

const useCashdeskBlocks = ({
  initialPayload, offerCount, onAddOffer, onOfferUsed, onOfferIndicesChange, onOfferRemoved, onPayloadChange,
}: UseCashdeskBlocksOptions) => {
  const uid = useId();
  const nextId = useRef(0);
  const [blocks, setBlocks] = useState<CashdeskBlock[]>(() => readBlocks(initialPayload));
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const onPayloadChangeRef = useLatestRef(onPayloadChange);
  const onOfferIndicesChangeRef = useLatestRef(onOfferIndicesChange);

  useEffect(() => {
    onPayloadChangeRef.current?.({ type: 'cashdesk', blocks });
    const indices = new Set<number>();
    blocks.forEach(block => {
      const index = offerIndexOf(block.anchor);
      if (index !== null) indices.add(index);
    });
    onOfferIndicesChangeRef.current?.(indices);
  }, [blocks, onPayloadChangeRef, onOfferIndicesChangeRef]);

  const selected = blocks.find(block => block.id === selectedId) ?? null;

  const addBlock = (block: NewBlock) => {
    const id = `block_${uid}_${nextId.current++}`;
    setBlocks(prev => [...prev, buildBlock(id, blocks.length + 1, block)]);
    setSelectedId(id);
  };

  const updateBlock = (id: string, patch: Partial<CashdeskBlock>) => {
    setBlocks(prev => prev.map(block => (block.id === id ? { ...block, ...patch } : block)));
  };

  const removeBlock = (id: string) => {
    const offerIndex = offerIndexOf(blocks.find(block => block.id === id)?.anchor);
    if (offerIndex !== null) onOfferRemoved?.(offerIndex);
    setBlocks(prev => prev.filter(block => block.id !== id));
    setSelectedId(prev => (prev === id ? null : prev));
  };

  const addBarcodeBlock = (anchor: string) => {
    addBlock({ type: 'barcode', content: anchor, anchor, width: 210, height: 75 });
    notifyOfferTokenInserted(anchor, { offerCount, onOfferUsed, onAddOffer });
  };

  const insertPersonalField = (anchor: string) => {
    if (selected?.type === 'text') updateBlock(selected.id, { content: selected.content + anchor });
    else addBlock({ type: 'text', content: anchor, width: 200, height: 40 });
  };

  return { blocks, selected, selectedId, setSelectedId, addBlock, updateBlock, removeBlock, addBarcodeBlock, insertPersonalField };
};

export default useCashdeskBlocks;
